/**
 * Notes. Because reducers use Immer, you can't just console.log pieces of your store. You'll get a bunch of proxy
 * stuff from the WritableDraft<> template. Wrap your data in unImmer() to print it out normally.
 */

import { ActionReducerMapBuilder, unImmer, CMD } from "../../../../common/redux/createReducer";
import { actions } from "./actions";
import { WindowServiceStore } from "../types";
import { normalizeWindowStateAfterTabbing } from "./windowTitleBar";
import { sendCommandToWindowService, updateGroupsAndWorkspace, removeFromTabCollection } from "./main";
import { Rectangle, WindowState } from "../../../../FEA";
import uuidv4 from "uuid-random";

const getInitialState = (): {} => {
	return {};
};

/**
 * Creates a tabCollection of a list of windows. If any windows are already in a collection, they
 * will be removed from it.
 */
export const createTabCollection = ({
	state,
	windowNames,
	tabCollectionId,
	promotedWindowName,
	cmd,
}: {
	state: WindowServiceStore;
	windowNames: string[];
	tabCollectionId: string;
	promotedWindowName?: string;
	cmd: CMD;
}) => {
	// Remove tabs from any current collection
	windowNames.forEach((windowName) => removeFromTabCollection({ state, windowName, cmd }));

	//Create a new tab collection for the passed in windows
	state.windowTitleBar.tabCollections[tabCollectionId] = {
		tabCollectionId,
		tabs: [...windowNames],
		promotedWindowName: promotedWindowName ?? windowNames[0],
		scroll: 0,
	};
	windowNames.forEach((windowName) => {
		if (state.windowTitleBar.windows[windowName]) {
			state.windowTitleBar.windows[windowName].tabCollectionId = tabCollectionId;
		}
	});
};

/**
 * Adds a list of windows to an existing tabCollection.
 * If position is specified all windows will be spliced into that location in array order,
 * otherwise they will be placed at the end.
 */
export const addToTabCollection = ({
	state,
	windowNames,
	tabCollectionId,
	position,
	cmd,
}: {
	state: WindowServiceStore;
	windowNames: string[];
	tabCollectionId: string;
	position?: number;
	cmd: CMD;
}) => {
	const destinationTabCollection = state.windowTitleBar.tabCollections[tabCollectionId];
	// Use the promoted window from the collection for any current window state
	const currentPromotedWindowName = state.windowTitleBar.tabCollections[tabCollectionId]?.promotedWindowName;
	if (!destinationTabCollection) return;

	// Remove the tab from its current collection
	windowNames.forEach((windowName) => removeFromTabCollection({ state, windowName, cmd }));

	// Add the tab(s) to the end if no position is specified
	if (!position) position = destinationTabCollection.tabs.length;
	// Add the dragged tab into the destination collection at the desired position (don't remove any tabs from the destination!)
	destinationTabCollection.tabs.splice(position, 0, ...windowNames);
	windowNames.forEach((windowName) => {
		normalizeWindowStateAfterTabbing({
			state,
			windowName,
			destinationWindowName: currentPromotedWindowName,
			tabCollectionId,
		});
	});
};

/**
 * Effects run when a tab collection is created or tabs are added to a collection.
 * Handles moving any window locations and setting the promoted tab.
 */
export const runTabCollectionEffects = ({
	state,
	tabCollectionId,
	boundsWindow,
	cmd,
}: {
	state: WindowServiceStore;
	tabCollectionId: string;
	boundsWindow?: string;
	cmd: CMD;
}) => {
	// Use either the supplied boundsWindow, or otherwise the promoted window to determine the dimensions of the tab collection
	const tabCollection = state.windowTitleBar.tabCollections[tabCollectionId];
	const promotedWindowName = tabCollection?.promotedWindowName;
	const physicalBoundsWindow = state.main.windows[boundsWindow ?? promotedWindowName ?? ""];
	const bounds = physicalBoundsWindow?.physicalWindowState?.bounds;
	if (!bounds) return;

	// Always move the current window to the same location as the destination window
	tabCollection.tabs.forEach((windowName) => {
		cmd.run(sendCommandToWindowService, {
			args: [
				windowName,
				"setBounds",
				{
					bounds: {
						top: bounds.y,
						left: bounds.x,
						width: bounds.width,
						height: bounds.height,
					},
				},
			],
		});
		// Show the promoted tab, hide all the others
		if (windowName === promotedWindowName) {
			cmd.run(sendCommandToWindowService, {
				args: [windowName, "show"],
			});
		} else {
			cmd.run(sendCommandToWindowService, {
				args: [windowName, "hide"],
			});
		}
	});
};

/**
 * Moves a window to a specified location. This is used when removing a tab from a tabCollection.
 * If the window is maximized then first unmaximize it before setting its bounds
 */
const moveWindowToLocation = async (
	windowName: string,
	bounds: Rectangle | null,
	windowState: WindowState,
	visibility: boolean
) => {
	// Restore the window if is it is physically maximized.
	if (windowState === "maximized") {
		await sendCommandToWindowService(windowName, "restore");
	}
	if (bounds) {
		// Update the bounds. This will also perform the restore on emulated maximized windows
		await sendCommandToWindowService(windowName, "setBounds", {
			bounds: {
				top: bounds?.y,
				left: bounds?.x,
				width: bounds?.width,
				height: bounds?.height,
			},
		});
	}

	if (visibility) {
		await sendCommandToWindowService(windowName, "show");
	} else {
		await sendCommandToWindowService(windowName, "hide");
	}
};

/**
 * Returns true if all windows allow tabbing, otherwise false.
 */
const allWindowsAllowTabbing = (state: WindowServiceStore, windowNames: string[]) =>
	windowNames.every((windowName) => {
		const titleBarData = state.windowTitleBar.windows[windowName];
		if (titleBarData === undefined) {
			console.error(
				`The state.windowTitleBar.windows["${windowName}"] is undefined because the windowTitleBar has not been registered yet.`
			);
			return false;
		}
		return titleBarData.enableTabbing;
	});

const addReducerCases = (builder: ActionReducerMapBuilder<WindowServiceStore>) => {
	builder
		/**
		 * Creates a tabCollection with a list of windows.
		 */
		.addCase(actions.createTabCollection, (state, action, cmd) => {
			const { windowNames, promotedWindowName, tabCollectionId } = action.payload;
			if (windowNames && allWindowsAllowTabbing(state, windowNames)) {
				// A tabCollectionId is only be passed in if create is called when spawning an older stack saved in the workspace.
				// If this is called from an API call, then we generate an ID but we use the same format that workspace is expecting
				const collectionId = tabCollectionId ?? `StackedWindow-${uuidv4()}`;
				createTabCollection({
					state,
					windowNames,
					tabCollectionId: collectionId,
					promotedWindowName,
					cmd,
				});
				runTabCollectionEffects({
					state,
					tabCollectionId: collectionId,
					cmd,
				});
				updateGroupsAndWorkspace(state, cmd);
			}
		})
		// Adds one or more tabs to an existing tabCollection
		.addCase(actions.addTabsToTabCollection, (state, action, cmd) => {
			const { windowNames, tabCollectionId, position, promotedWindowName } = action.payload;
			if (!tabCollectionId) {
				return;
			}
			const tabCollection = state.windowTitleBar.tabCollections?.[tabCollectionId];
			if (windowNames && tabCollection && allWindowsAllowTabbing(state, windowNames)) {
				// First add the new windows to the tab collection. Don't change the promoted window. Don't worry about sizes.
				addToTabCollection({
					state,
					windowNames,
					tabCollectionId,
					position,
					cmd,
				});
				// We don't want to change the dimensions of the tab collection, so remember the current promoted window in order to get its bounds
				const boundsWindow = tabCollection.promotedWindowName;

				// Maybe set the promoted window to the specified window
				if (promotedWindowName) tabCollection.promotedWindowName = promotedWindowName;

				// Run effects to resize the new windows and show/hide as necessary
				runTabCollectionEffects({
					state,
					tabCollectionId,
					boundsWindow,
					cmd,
				});
				updateGroupsAndWorkspace(state, cmd);
			}
		})
		// Removes one or more tabs from a tabCollection
		.addCase(actions.removeTabsFromTabCollection, (state, action, cmd) => {
			const { windowNames, visibility } = action.payload;
			windowNames?.forEach((windowName: string) => {
				removeFromTabCollection({ state, windowName, cmd });

				// Restore to its pre-maximized size if maximized, or otherwise its current size
				// physicalWindowState.normalBounds will not exist in some cases (i.e. A child window in a collection loaded from a workspace)
				// so we have a third fallback to bounds which should always be present.
				const bounds =
					state.main.maximization[windowName]?.normalBounds ??
					state.main.windows[windowName]?.physicalWindowState.normalBounds ??
					state.main.windows[windowName]?.physicalWindowState.bounds;

				if (state.windowTitleBar.windows[windowName]) state.windowTitleBar.windows[windowName].maximized = false;
				delete state.main.maximization[windowName];
				cmd.run(moveWindowToLocation, {
					args: [
						windowName,
						unImmer(bounds) ?? null,
						state.main.windows[windowName]?.physicalWindowState.windowState,
						visibility,
					],
				});
			});
			updateGroupsAndWorkspace(state, cmd);
		});
};

export default {
	getInitialState,
	addReducerCases,
	actions,
};
